import type { SessionEventCategory } from '@repo/types/sessions/session-types';
import type * as MapLibre from 'maplibre-gl';

export const TRAIL_SOURCE_ID = 'unified-trail' as const;
export const TELEPORT_SOURCE_ID = 'unified-teleport' as const;
export const ACTIVE_SOURCE_ID = 'unified-trail-active' as const;

export const LAYER_IDS = {
    TRAIL: 'unified-trail-line',
    ACTIVE_TRAIL: 'unified-trail-active-line',
    TELEPORT_LINE: 'unified-teleport-line',
    TELEPORT_MARKER: 'unified-teleport-marker',
    START: 'unified-start-marker',
    START_INNER: 'unified-start-marker-inner',
    FINISH: 'unified-finish-marker',
    FINISH_ICON: 'unified-finish-marker-icon',
    CURRENT: 'unified-current-marker',
    HEAD: 'unified-head-marker',
} as const;

export const COLOR_TRAIL_SOLID = 'rgba(231,255,12,1)';
export const COLOR_TRAIL_TRANSPARENT = 'rgba(231,255,12,0)';

export const COLOR_START = COLOR_TRAIL_SOLID;
export const COLOR_FINISH = COLOR_TRAIL_SOLID;
export const COLOR_TELEPORT_LINE = COLOR_TRAIL_SOLID;
export const COLOR_TELEPORT_MARKER = COLOR_TRAIL_SOLID;
export const COLOR_START_INNER = '#000000';
export const COLOR_CURRENT = '#ff4444';
export const COLOR_HEAD = '#FF6B35';
export const COLOR_STROKE = '#FFFFFF';

export const OPACITY_START = 1;
export const OPACITY_FINISH = 1;
export const OPACITY_CURRENT = 0.8;
export const OPACITY_HEAD = 0.95;
export const OPACITY_TELEPORT_MARKER = 1;

export const STROKE_WIDTH_START = 0;
export const STROKE_WIDTH_FINISH = 0;
export const STROKE_WIDTH_CURRENT = 2.5;
export const STROKE_WIDTH_HEAD = 2.5;

export const RADIUS_START = 12;
export const RADIUS_START_INNER = 6;
export const RADIUS_FINISH = 18;
export const RADIUS_CURRENT = 7;
export const RADIUS_TELEPORT_MARKER = 8;

export const LINE_WIDTH_BASE = 1.5;
export const LINE_WIDTH_ZOOM_MIN = 8;
export const LINE_WIDTH_MIN = 6;
export const LINE_WIDTH_ZOOM_MAX = 16;
export const LINE_WIDTH_MAX = 10;
/** Dash pattern for teleport lines: [dash, gap] in line-width multiples. */
export const TELEPORT_LINE_DASH: [number, number] = [2, 2];

export const HEAD_RADIUS_ZOOM_MIN = 8;
export const HEAD_RADIUS_MIN = 6;
export const HEAD_RADIUS_ZOOM_MAX = 16;
export const HEAD_RADIUS_MAX = 11;

export const EPS_GRADIENT = 1e-6;

export const PROP_TYPE = 'type' as const;
export const TYPE_TRAIL = 'trail' as const;
export const TYPE_START = 'start' as const;
export const TYPE_FINISH = 'finish' as const;
export const TYPE_CURRENT = 'current' as const;
export const TYPE_HEAD = 'head' as const;
export const TYPE_TELEPORT_LINE = 'teleport-line' as const;
export const TYPE_TELEPORT_MARKER = 'teleport-marker' as const;

export const FLAG_ICON_SIZE = 0.65;
export const FLAG_SIZE_MULTIPLIER = 1.2;

export const EVENT_SOURCE_ID = 'session-events' as const;
export const EVENT_CIRCLE_LAYER_ID = 'session-event-circles' as const;
export const EVENT_ICON_LAYER_ID = 'session-event-icons' as const;

export const EVENT_CIRCLE_RADIUS = 10;
export const EVENT_CIRCLE_STROKE_WIDTH = 1.5;
export const EVENT_CIRCLE_STROKE_COLOR = '#FFFFFF';
export const EVENT_CIRCLE_OPACITY = 0.9;

export const EVENT_CATEGORY_COLORS: Record<SessionEventCategory, string> = {
    deaths: '#F44336',
    general: '#78909C',
    kills: '#E53935',
    objective_success: '#43A047',
    objective_fail: '#EF5350',
    achievements: '#EF6C00',
    mod: '#AB47BC',
};

export const FLAG_SVG = `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <mask id="mask0_17122_17252" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="32" height="32">
        <rect width="32" height="32" fill="#D9D9D9"/>
    </mask>
    <g mask="url(#mask0_17122_17252)">
        <path d="M6.66699 28.0007V5.33398H18.667L19.2003 8.00065H26.667V21.334H17.3337L16.8003 18.6673H9.33366V28.0007H6.66699Z" fill="#1C1B1F"/>
    </g>
</svg>`;

/**
 * Build a MapLibre line-gradient expression for progressive trail reveal
 */
export function buildLineGradientExpression(reveal: number): MapLibre.ExpressionSpecification {
    const r = Math.max(0, Math.min(1, reveal));

    if (r <= EPS_GRADIENT) {
        return [
            'interpolate',
            ['linear'],
            ['line-progress'],
            0,
            COLOR_TRAIL_TRANSPARENT,
            1,
            COLOR_TRAIL_TRANSPARENT,
        ] as unknown as MapLibre.ExpressionSpecification;
    }

    if (r >= 1 - EPS_GRADIENT) {
        return [
            'interpolate',
            ['linear'],
            ['line-progress'],
            0,
            COLOR_TRAIL_SOLID,
            1,
            COLOR_TRAIL_SOLID,
        ] as unknown as MapLibre.ExpressionSpecification;
    }

    const stop1 = Math.max(EPS_GRADIENT, Math.min(1 - EPS_GRADIENT, r));
    const stop2 = Math.min(1 - EPS_GRADIENT / 2, stop1 + EPS_GRADIENT);

    return [
        'interpolate',
        ['linear'],
        ['line-progress'],
        0,
        COLOR_TRAIL_SOLID,
        stop1,
        COLOR_TRAIL_SOLID,
        stop2,
        COLOR_TRAIL_TRANSPARENT,
    ] as unknown as MapLibre.ExpressionSpecification;
}
